import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, ChevronLeft, ChevronRight, Search, X } from "lucide-react";

import api from "@/api/axios";

type Failure = {
  id: number;
  user_id: string;
  user_nickname: string;
  telegram_id: string;
  group_name: string | null;
  chat_id: number | null;
  status: string;
  error_type: string | null;
  retry_after: number | null;
  created_at: string;
};

const statusOptions = [
  { value: "", label: "全部异常" },
  { value: "write_forbidden", label: "群组禁止发言" },
  { value: "not_in_group", label: "未加入群组" },
  { value: "account_write_forbidden", label: "账号禁止发言" },
  { value: "rate_limited", label: "Telegram 限流" },
  { value: "slow_mode", label: "群组慢速模式" },
  { value: "group_banned", label: "群组禁止该账号发言" },
  { value: "telegram_forbidden", label: "Telegram 拒绝发送" },
  { value: "invalid_peer", label: "发送目标无效" },
  { value: "chat_restricted", label: "群组受限" },
  { value: "topic_closed", label: "话题已关闭" },
  { value: "payment_required", label: "需要付费才能发送" },
  { value: "request_failed", label: "请求多次失败" },
  { value: "other_error", label: "发送失败" },
  { value: "unknown_error", label: "未知错误" },
  { value: "exception", label: "发送异常" },
];
const statusNames = Object.fromEntries(statusOptions.map((item) => [item.value, item.label]));
const formatTime = (value: string) => new Date(value).toLocaleString("zh-CN", { hour12: false });

export function AdminCheckinFailures() {
  const [period, setPeriod] = useState("24h");
  const [status, setStatus] = useState("");
  const [searchText, setSearchText] = useState("");
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const report = useQuery({
    queryKey: ["admin", "checkin-failures", period, status, keyword, page],
    queryFn: async () => (await api.get("/api/admin/checkin-failures", {
      params: { period, status, keyword, page, page_size: 20 },
    })).data.data as { total: number; items: Failure[] },
  });
  const search = () => { setPage(1); setKeyword(searchText.trim()); };
  const clear = () => { setSearchText(""); setKeyword(""); setPage(1); };

  return <>
    <div className="admin-page-heading"><div><h1>异常明细</h1></div><div className="admin-page-heading__count"><AlertTriangle size={18} /> 共 {report.data?.total ?? "—"} 条</div></div>
    <section className="admin-panel admin-list-panel"><div className="admin-failure-toolbar">
      <div className="admin-field"><label htmlFor="failure-period">时间范围</label><select id="failure-period" value={period} onChange={(event) => { setPeriod(event.target.value); setPage(1); }}><option value="24h">最近 24 小时</option><option value="7d">最近 7 天</option><option value="30d">最近 30 天</option></select></div>
      <div className="admin-field"><label htmlFor="failure-status">异常类型</label><select id="failure-status" value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}>{statusOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></div>
      <div className="admin-field admin-failure-toolbar__search"><label htmlFor="failure-keyword">用户或群组</label><div className="admin-search"><Search size={18} /><input id="failure-keyword" value={searchText} onChange={(event) => setSearchText(event.target.value)} onKeyDown={(event) => event.key === "Enter" && search()} placeholder="昵称、Telegram 用户名或群组" />{searchText && <button onClick={clear} aria-label="清空搜索"><X size={16} /></button>}</div></div>
      <button className="admin-button admin-button--primary" onClick={search}>搜索 <ArrowRight size={16} /></button>
    </div>
      {report.isError && <div className="admin-failure-error">异常记录加载失败，请稍后重试。</div>}
      <div className="admin-table-wrap"><table className="admin-table admin-failure-table"><thead><tr><th>发生时间</th><th>用户</th><th>群组</th><th>异常类型</th><th>补充信息</th></tr></thead><tbody>{report.data?.items.map((item) => <tr key={item.id}><td>{formatTime(item.created_at)}<span className="admin-failure-tag admin-failure-tag--mobile">{statusNames[item.status] || item.status}</span></td><td><strong className="admin-table__primary">{item.user_nickname}</strong><span className="admin-table__secondary">{item.telegram_id}</span></td><td><strong className="admin-table__primary">{item.group_name || "未知群组"}</strong><span className="admin-table__secondary">{item.chat_id ?? "—"}</span></td><td><span className="admin-failure-tag">{statusNames[item.status] || item.status}</span></td><td>{[item.error_type, item.retry_after != null ? `建议等待 ${item.retry_after} 秒` : null].filter(Boolean).join(" · ") || "—"}</td></tr>)}</tbody></table>
        {report.isLoading && <div className="admin-table-message">正在加载异常记录…</div>}
        {!report.isLoading && !report.isError && !report.data?.items.length && <div className="admin-table-message">所选条件下没有群组发送异常。</div>}
      </div>
      <div className="admin-pagination"><span>第 {page} 页 · 共 {report.data?.total ?? 0} 条</span><div><button disabled={page <= 1} onClick={() => setPage((value) => value - 1)} aria-label="上一页"><ChevronLeft size={18} /></button><button disabled={page * 20 >= (report.data?.total ?? 0)} onClick={() => setPage((value) => value + 1)} aria-label="下一页"><ChevronRight size={18} /></button></div></div>
    </section>
    <p className="admin-failure-note">只统计已入库的群组发送失败事件，不包含缺少会话、会员到期等调度级失败；已入库的旧未知错误会按异常类型显示，未入库记录无法回填。逐次事件保留 180 天。</p>
  </>;
}
